import { useContext, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/src/shadComponents/ui/button';
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/src/shadComponents/ui/form';
import { Input } from '@/src/shadComponents/ui/input';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@/src/shadComponents/ui/card';
import useWhitelabelSettings from './hooks/useWhitelabelSettings';
import OrgContext from '../../Authentication/components/OrgContext';
import PageShell from '@/src/Common/components/PageShell';

// Define form validation schema
const formSchema = z.object({
    name: z
        .string()
        .min(2, 'Name must be at least 2 characters')
        .max(100, 'Name must be 100 characters or less'),
    emailFromAddress: z.string().email('Invalid email address'),
    emailReplyToAddress: z.string().email('Invalid email address'),
    emailFromName: z
        .string()
        .min(2, 'Name must be at least 2 characters')
        .max(100, 'Name must be 100 characters or less'),
    domain: z.string().url('Invalid URL format. Include http:// or https://'),
});

type FormValues = z.infer<typeof formSchema>;

export default function WhitelabelSettingsForm() {
    const { id } = useParams<{ id: string }>();
    const isEditing = !!id;
    const [loading, setLoading] = useState(isEditing);
    const navigate = useNavigate();
    const { org } = useContext(OrgContext);
    const {
        getWhitelabelSettings,
        createWhitelabelSettings,
        updateWhitelabelSettings,
    } = useWhitelabelSettings();

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            name: '',
            emailFromAddress: '',
            emailReplyToAddress: '',
            emailFromName: '',
            domain: '',
        },
    });

    useEffect(() => {
        // If editing, fetch the current settings
        if (isEditing && id) {
            const fetchSettings = async () => {
                try {
                    setLoading(true);
                    const settings = await getWhitelabelSettings(
                        parseInt(id, 10),
                        org?.id,
                    );

                    // Populate the form
                    form.reset({
                        name: settings.name,
                        emailFromAddress: settings.email_from_address,
                        emailReplyToAddress: settings.email_reply_to_address,
                        emailFromName: settings.email_from_name,
                        domain: settings.domain,
                    });
                } catch (error) {
                    console.error('Error fetching whitelabel settings:', error);
                    toast.error('Failed to load whitelabel settings');
                    // Navigate back on error
                    navigate('/org/whitelabel-settings');
                } finally {
                    setLoading(false);
                }
            };

            fetchSettings();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSubmit = async (values: FormValues) => {
        try {
            setLoading(true);

            if (isEditing && id) {
                // Update existing settings
                await updateWhitelabelSettings(
                    parseInt(id, 10),
                    org?.id,
                    values,
                );
                toast.success('Whitelabel settings updated successfully');
            } else {
                // Create new settings
                if (!org?.id) {
                    toast.error('Organization information is required');
                    return;
                }

                await createWhitelabelSettings(org.id, {
                    ...values,
                    orgId: org.id,
                });
                toast.success('Whitelabel settings created successfully');
            }

            // Redirect back to settings list
            navigate('/org/whitelabel-settings');
        } catch (error) {
            console.error('Error saving whitelabel settings:', error);
            toast.error(
                isEditing
                    ? 'Failed to update whitelabel settings'
                    : 'Failed to create whitelabel settings',
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <PageShell
            title="Whitelabel Settings"
            breadcrumbs={[
                { label: 'Whitelabel', href: '/org/whitelabel-settings' },
                { label: 'Settings' },
            ]}
        >
            <Card className="border-none shadow-none">
                <CardHeader>
                    <CardTitle>
                        {isEditing ? 'Edit Settings' : 'New Settings'}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <Form {...form}>
                        <form
                            onSubmit={form.handleSubmit(onSubmit)}
                            className="space-y-6"
                        >
                            <FormField
                                control={form.control}
                                name="name"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Name</FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder="My Brand Settings"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormDescription>
                                            A name to identify these whitelabel
                                            settings
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="domain"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Domain</FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder="https://gallery.yourdomain.com"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormDescription>
                                            The custom domain for your gallery
                                            (must include http:// or https://)
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="emailFromName"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Email From Name</FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder="Your Brand Name"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormDescription>
                                            The name that appears as the sender
                                            of emails
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="emailFromAddress"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>
                                            Email From Address
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder="photos@yourdomain.com"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormDescription>
                                            The email address that appears as
                                            the sender
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="emailReplyToAddress"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>
                                            Email Reply-To Address
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder="support@yourdomain.com"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormDescription>
                                            The email address for recipients to
                                            reply to
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <div className="flex justify-end space-x-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() =>
                                        navigate('/org/whitelabel-settings')
                                    }
                                    disabled={loading}
                                >
                                    Cancel
                                </Button>
                                <Button type="submit" disabled={loading}>
                                    {loading ? (
                                        <>
                                            <Loader2 className="size-4 mr-2 animate-spin" />
                                            {isEditing
                                                ? 'Updating...'
                                                : 'Creating...'}
                                        </>
                                    ) : (
                                        <>{isEditing ? 'Update' : 'Create'}</>
                                    )}
                                </Button>
                            </div>
                        </form>
                    </Form>
                </CardContent>
            </Card>
        </PageShell>
    );
}
